import React from "react";
import { Link } from "react-router-dom";
import { AIPageShell, AIVideo, AIDiagram } from "./DualAIShared";
import hotelImage from "./assets/ai-engeeningr/hotel.png";
import realEstateImage from "./assets/ai-engeeningr/realestate.jpg";
import weddingImage from "./assets/ai-engeeningr/mariage.png";
import footImage from "./assets/ai-engeeningr/foot.png";
import ragVideo from "./assets/ai-engeeningr/rag.mp4";
import aiGuardVideo from "./assets/ai-engeeningr/aiguard.mp4";

const workflowSteps = [
  ["01", "AI Proposal", "Start with a story proposal, use-case frame or Weave concept generated from a short business or creator brief."],
  ["02", "AI Connection", "Retrieve relevant project and business evidence so the next model actions stay grounded in trusted context."],
  ["03", "AI Generation", "Create narrative blocks, images, structure proposals or editing instructions with bounded output contracts."],
  ["04", "Connective AI", "Recommend links between media, suggest improvements and infer how existing assets can become a coherent Weave."],
  ["05", "AI Guard", "Classify content, flag risk and route uncertain outputs to review before publication or payout logic is triggered."],
  ["06", "Human review", "Keep creators, operators or moderators in the loop when judgment, quality control or policy confirmation is required."],
];

const guardDecisions = [
  ["ALLOW", "Content can continue in the publishing workflow with no further intervention."],
  ["REVIEW", "Uncertain content is routed to a human reviewer with the supporting context preserved."],
  ["BLOCK", "Inappropriate or out-of-policy content is stopped before it can enter the next workflow state."],
];

const creationCards = [
  {
    label: "AI PROPOSAL",
    title: "Turn intent into a structured creative starting point.",
    text: "Generate the first concept, story angle, contributor brief and slot direction from a short use-case description.",
    image: weddingImage,
  },
  {
    label: "AI GENERATION",
    title: "Produce assets and instructions the workflow can consume.",
    text: "Generate text, images and prompt-based transformations that remain bounded by product logic and versioning rules.",
    image: hotelImage,
  },
];


const operatingModel = [
  ["Control plane", "Own routing, policy, approval gates and workflow state independently from the model that performs the task."],
  ["Model plane", "Execute reasoning, generation, vision or moderation through replaceable task-specific model services."],
  ["Data plane", "Keep project context, retrieved evidence, media metadata and generated versions governed by the application rather than embedded inside model state."],
  ["Resilience", "Design retries, fallbacks and manual continuation paths so one failed AI call does not block the complete production workflow."],
  ["Operational telemetry", "Track latency, error rate, moderation outcomes and human-review volume to understand both technical health and automation value."],
  ["Human override", "Preserve explicit accept, reject, edit and escalate actions wherever policy, quality or financial impact requires accountable judgment."],
];

const intelligenceCards = [
  {
    label: "CONNECTIVE AI",
    title: "Find narrative links across existing media.",
    text: "Surface media relationships, quick-edit opportunities and storyline continuity to accelerate Weave assembly.",
    image: realEstateImage,
  },
  {
    label: "AI PROMPT EDITING",
    title: "Guide transformation with controlled natural language.",
    text: "Use prompt editing to request a change, preserve the important visual elements and generate a reviewable new version.",
    image: footImage,
  },
];

function EditorialCard({ item, reverse = false }) {
  return (
    <article className={`dai-dualAIEditorialCard ${reverse ? "is-reverse" : ""}`}>
      <figure>
        <img src={item.image} alt="" loading="lazy" />
      </figure>
      <div className="dai-dualAIEditorialCopy">
        <span className="dai-cardLabel">{item.label}</span>
        <h3>{item.title}</h3>
        <p>{item.text}</p>
      </div>
    </article>
  );
}

export default function DualAIWorkflowPage() {
  return (
    <AIPageShell
      pageTitle="AI Workflow"
      currentPath="/dual-ai/workflow"
      eyebrow="WEAVEVO AI / AI WORKFLOW"
      heroTitle="One AI workflow from proposal to trust-controlled publication."
      heroText="Weavevo AI Workflow coordinates proposal generation, grounded retrieval, creative generation, media connection and AI Guard inside one operational chain so teams can automate without losing control."
      finalEyebrow="AI WORKFLOW"
      finalTitle="Build the workflow once and let each AI function play its precise role."
      finalText="Proposal, connection, generation, recommendation, moderation and human review become much more valuable when they behave as stages of the same workflow."
    >
      <section className="dai-section dai-dualAIIntroSection">
        <div className="dai-dualAISectionIntro">
          <span className="dai-sectionEyebrow">END-TO-END ORCHESTRATION</span>
          <h2>Six stages describe how Weavevo AI behaves in production.</h2>
          <p>
            The workflow is not linear because every project follows the exact same path. But these stages define the
            core choreography from idea creation to publication-safe execution.
          </p>
        </div>
        <div className="dai-aiUseCaseRail dai-aiUseCaseRailThree">
          {workflowSteps.map(([number, title, text]) => (
            <article key={number}>
              <span>{number}</span>
              <h3>{title}</h3>
              <p>{text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="dai-section dai-dualAIIntroSection">
        <div className="dai-dualAISectionIntro">
          <span className="dai-sectionEyebrow">CREATION ENTRY POINTS</span>
          <h2>Start with idea generation, then move into execution.</h2>
        </div>
        <div className="dai-dualAIEditorialStack">
          {creationCards.map((item, index) => (
            <EditorialCard item={item} reverse={index % 2 === 1} key={item.label} />
          ))}
        </div>
      </section>

      <section className="dai-section dai-dualAIDarkSection dai-dualAIRagSection">
        <div className="dai-dualAIVideoStory">
          <div className="dai-dualAIStoryCopy">
            <span className="dai-sectionEyebrow">AI CONNECTION</span>
            <h2>Ground the workflow in project and business knowledge before execution.</h2>
            <p>
              Retrieval-Augmented Generation supplies the runtime context required for accurate recommendations,
              structured proposals and trustworthy AI decisions.
            </p>
          </div>
          <AIVideo src={ragVideo} label="AI CONNECTION / RAG" />
        </div>
      </section>

      <AIDiagram kind="guard" />

      <section className="dai-section dai-dualAIGuardSection">
        <div className="dai-dualAIGuardGrid">
          <AIVideo src={aiGuardVideo} label="AI GUARD" />
          <div className="dai-dualAIGuardCopy">
            <span className="dai-sectionEyebrow">AI GUARD</span>
            <h2>Protect the publishing flow before content goes live.</h2>
            <p>
              Submitted or generated media passes through an AI-assisted control layer before publication, with human
              review preserved whenever the decision is uncertain or policy-sensitive.
            </p>
            <div className="dai-dualAIDecisions">
              {guardDecisions.map(([label, text]) => (
                <article key={label} data-state={label.toLowerCase()}>
                  <strong>{label}</strong>
                  <span>{text}</span>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="dai-section dai-dualAIWeaveSection">
        <div className="dai-dualAISectionIntro">
          <span className="dai-sectionEyebrow">POST-GENERATION INTELLIGENCE</span>
          <h2>Move from generated ideas to connected and editable media.</h2>
        </div>
        <div className="dai-dualAIEditorialStack">
          {intelligenceCards.map((item, index) => (
            <EditorialCard item={item} reverse={index % 2 === 0} key={item.label} />
          ))}
        </div>
      </section>

      <section className="dai-section dai-aiEnterpriseSection">
        <div className="dai-aiHubSectionLead dai-aiHubSectionLeadCompact">
          <span className="dai-sectionEyebrow">ENTERPRISE OPERATING MODEL</span>
          <h2>Separate control, models and data so the AI workflow can evolve safely.</h2>
          <p>For architecture and technical-sales discussions, the key question is not which model is fashionable. It is whether the workflow can change models, recover from failures, preserve governance and expose measurable operational value.</p>
        </div>
        <div className="dai-aiCapabilityMatrix">
          {operatingModel.map(([title, text]) => (
            <article key={title}>
              <h3>{title}</h3>
              <p>{text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="dai-section dai-aiGenerationControl">
        <div className="dai-aiGenerationControlGrid">
          <div>
            <span className="dai-sectionEyebrow">WORKFLOW VALUE</span>
            <h2>AI becomes enterprise-ready when each output is attached to the next action.</h2>
            <p>
              The business value is not just that models generate content. The value is that every result has a place:
              a proposal, a media improvement, a recommendation, a moderation decision or a human review task.
            </p>
            <Link className="dai-aiTextLink dai-aiTextLinkDark" to="/dual-ai">
              Return to the AI overview <span aria-hidden="true">→</span>
            </Link>
          </div>
          <div className="dai-aiGenerationControlCards">
            <article><strong>Traceability</strong><span>Keep source context, generation state and reviewer decisions attached to the result.</span></article>
            <article><strong>Interoperability</strong><span>Let proposal, connection, generation and moderation outputs feed each other without re-entering data manually.</span></article>
            <article><strong>Human override</strong><span>Always preserve the ability to accept, reject, edit or escalate an AI-generated result.</span></article>
            <article><strong>Operational trust</strong><span>Ensure automation never bypasses moderation, versioning or approval rules that matter to the business.</span></article>
          </div>
        </div>
      </section>
    </AIPageShell>
  );
}
